import { TextField } from "@mui/material";
import { useRef, useState } from "react";

import { DEFAULT_SKILL_FILE_EXTENSION, type SkillEntryKind } from "./skillFilePaths";

type SkillTreeNameInputProps = {
  kind: SkillEntryKind;
  initialName: string;
  error: string | null;
  /** Leave the input open when it loses focus while still empty. */
  keepOnEmptyBlur: boolean;
  onChange: () => void;
  /** Resolves false when the name was refused, keeping the input open. */
  onCommit: (name: string) => Promise<boolean>;
  onCancel: () => void;
};

/** Select the name up to its extension, so typing replaces just the name. */
function selectBaseName(input: HTMLInputElement | HTMLTextAreaElement) {
  const dot = input.value.lastIndexOf(".");
  input.setSelectionRange(0, dot > 0 ? dot : input.value.length);
}

/** Enter or leaving the field saves the name; Escape cancels. */
export function SkillTreeNameInput({
  kind,
  initialName,
  error,
  keepOnEmptyBlur,
  onChange,
  onCommit,
  onCancel,
}: SkillTreeNameInputProps) {
  const [name, setName] = useState(initialName);
  // Enter commits, then the row unmounts and blurs; this stops a second commit.
  const settling = useRef(false);

  const commit = async () => {
    if (settling.current) {
      return;
    }
    settling.current = true;
    const accepted = await onCommit(name);
    settling.current = accepted;
  };

  const cancel = () => {
    settling.current = true;
    onCancel();
  };

  return (
    <TextField
      autoFocus
      fullWidth
      size="small"
      variant="standard"
      value={name}
      placeholder={kind === "file" ? `name${DEFAULT_SKILL_FILE_EXTENSION}` : "folder name"}
      error={Boolean(error)}
      helperText={error ?? undefined}
      onChange={(event) => {
        setName(event.target.value);
        onChange();
      }}
      onFocus={(event) => selectBaseName(event.target)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          void commit();
        } else if (event.key === "Escape") {
          // Escape would otherwise close the dialog.
          event.stopPropagation();
          cancel();
        }
      }}
      onBlur={() => {
        if (name.trim()) {
          void commit();
        } else if (!keepOnEmptyBlur) {
          cancel();
        }
      }}
      inputProps={{ "aria-label": kind === "file" ? "File name" : "Folder name" }}
      sx={{ "& .MuiInputBase-input": { fontSize: 14, py: 0.25 } }}
    />
  );
}
